import React, { useCallback, useEffect, useState } from "react";
import AppearanceButton from "../Appearance/AppearanceButton";
import {
  followCommunityEntity,
  unfollowCommunityEntity,
} from "./communitySocialApi";

/**
 * Follow / Following toggle for organization, creator, project, or user.
 * Persists via community/social/follow|unfollow; updates counts immediately.
 */
function CommunityFollowButton({
  type = "organization",
  id,
  initiallyFollowing = false,
  followerCount = null,
  onChange = null,
  className = "org-type-card__follow",
  disabled = false,
  showCount = false,
}) {
  const entityId = id != null ? Number(id) : 0;
  const [following, setFollowing] = useState(!!initiallyFollowing);
  const [count, setCount] = useState(
    typeof followerCount === "number" ? followerCount : null
  );
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    setFollowing(!!initiallyFollowing);
  }, [initiallyFollowing, entityId]);

  useEffect(() => {
    if (typeof followerCount === "number") {
      setCount(followerCount);
    }
  }, [followerCount, entityId]);

  const handleClick = useCallback(
    async (event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (!entityId || busy || disabled) return;

      const hasSession =
        localStorage.getItem("userId") && localStorage.getItem("accessToken");
      if (!hasSession) {
        window.location.href = "/login";
        return;
      }

      setBusy(true);
      setErrorMsg("");
      const wasFollowing = following;
      // Optimistic UI
      setFollowing(!wasFollowing);
      if (typeof count === "number") {
        setCount(Math.max(0, count + (wasFollowing ? -1 : 1)));
      }

      const result = wasFollowing
        ? await unfollowCommunityEntity(type, entityId)
        : await followCommunityEntity(type, entityId);

      if (result.error) {
        setFollowing(wasFollowing);
        if (typeof count === "number") {
          setCount(count);
        }
        setErrorMsg(result.message || "Unable to update follow");
        setBusy(false);
        return;
      }

      const nextFollowing = !!result.following;
      setFollowing(nextFollowing);
      if (typeof result.follower_count === "number") {
        setCount(result.follower_count);
      }
      setBusy(false);
      if (typeof onChange === "function") {
        onChange({
          following: nextFollowing,
          follower_count:
            typeof result.follower_count === "number"
              ? result.follower_count
              : count,
          type,
          id: entityId,
        });
      }
      try {
        window.dispatchEvent(
          new CustomEvent("community-follow-changed", {
            detail: {
              type,
              id: entityId,
              following: nextFollowing,
            },
          })
        );
      } catch (e) {
        /* ignore */
      }
    },
    [entityId, busy, disabled, following, count, type, onChange]
  );

  if (!entityId) {
    return null;
  }

  const label = following ? "Following" : "Follow";
  const title = following ? "Unfollow" : "Follow";

  return (
    <span className="community-follow-btn-wrap">
      <AppearanceButton
        type="button"
        variant={following ? "following" : "follow"}
        className={
          className +
          (following ? " is-following" : "") +
          (busy ? " is-busy" : "")
        }
        onClick={handleClick}
        disabled={busy || disabled}
        aria-pressed={following}
        title={title}
      >
        {label}
        {showCount && typeof count === "number" ? (
          <span className="community-follow-btn__count"> {count}</span>
        ) : null}
      </AppearanceButton>
      {errorMsg ? (
        <span className="community-follow-btn__error" role="alert">
          {errorMsg}
        </span>
      ) : null}
    </span>
  );
}

export default CommunityFollowButton;
